/**
 * Onglet "Informations" de la fiche GRV : présentation compacte des données générales, groupées par
 * carte plutôt qu'un champ par ligne, plus la modification de la fiche.
 *
 * `client_id` n'est modifiable que par un administrateur TotalEnergies côté serveur
 * (`CHAMPS_RESERVES_ADMIN`, `grv/service.py`) : le formulaire ne propose ce champ qu'à ce profil,
 * pour ne pas laisser un opérationnel croire qu'il peut réaffecter un GRV puis échouer en silence.
 *
 * Ni le changement de tracker, ni le changement de statut opérationnel ne se font plus ici
 * (`docs/DECISIONS.md` D89) : les deux ont leur propre formulaire dans l'onglet « Action », qui
 * historise systématiquement l'opération dans la fiche de vie.
 */
import { useState } from "react";

import { useIdentite } from "../../shared/auth/contexte";
import { ErreurApi } from "../../shared/api/erreurs";
import type { FicheGrv } from "../../shared/api/types";
import { Chargement, Localisation, ValeurOuRaison } from "../../shared/composants/Etats";
import { formaterDate } from "../../shared/composants/formatage";
import { useReferentiel } from "../admin/requetes";
import { optionsAvecValeurCourante, resoudreCode } from "./referentielFiche";
import { useMettreAJourGrv, useProduitsTransportes } from "./requetes";

function badgeStatut(statut: string) {
  const normalise = statut.toLowerCase();
  if (normalise.includes("service") || normalise.includes("actif")) return "badge badge--succes";
  if (normalise.includes("panne") || normalise.includes("hors") || normalise.includes("arret")) {
    return "badge badge--alerte";
  }
  return "badge";
}

export function OngletInformations({
  fiche,
  peutModifier,
}: {
  fiche: FicheGrv;
  peutModifier: boolean;
}) {
  const identite = useIdentite();
  const estAdmin = identite.profil === "admin-totalenergies";

  return (
    <div>
      <div className="grille-cartes">
        <dl className="carte">
          <h3>Exploitation</h3>
          <div className="carte__champ">
            <dt>Statut opérationnel</dt>
            <dd>
              <span className={badgeStatut(fiche.statut_operationnel)}>
                {fiche.statut_operationnel}
              </span>
            </dd>
          </div>
          {!identite.clientId && (
            <div className="carte__champ">
              <dt>Client</dt>
              <dd>{fiche.client_id}</dd>
            </div>
          )}
          <div className="carte__champ">
            <dt>Produit transporté</dt>
            <dd>{fiche.produit_transporte ?? "—"}</dd>
          </div>
          <div className="carte__champ">
            <dt>Localisation actuelle</dt>
            <dd>
              <Localisation zone={fiche.zone_courante_nom} agregee={fiche.localisation_agregee} />
            </dd>
          </div>
        </dl>

        <dl className="carte">
          <h3>Tracker</h3>
          <div className="carte__champ">
            <dt>Numéro de série</dt>
            <dd>{fiche.numero_serie_tracker ?? "—"}</dd>
          </div>
          <div className="carte__champ">
            <dt>Statut tracker</dt>
            <dd>{fiche.statut_tracker ?? "—"}</dd>
          </div>
          <div className="carte__champ">
            <dt>Dernière communication</dt>
            <dd>{formaterDate(fiche.derniere_communication)}</dd>
          </div>
          <div className="carte__champ">
            <dt>Batterie</dt>
            <dd>
              <ValeurOuRaison valeur={fiche.batterie_pct ?? null} unite="%" />
            </dd>
          </div>
        </dl>

        <dl className="carte">
          <h3>Conformité</h3>
          <div className="carte__champ">
            <dt>Mise en service</dt>
            <dd>{fiche.date_mise_en_service ?? "—"}</dd>
          </div>
          <div className="carte__champ">
            <dt>Dernière épreuve d'étanchéité</dt>
            <dd>{fiche.date_derniere_epreuve_etancheite ?? "—"}</dd>
          </div>
          <div className="carte__champ">
            <dt>Prochaine épreuve d'étanchéité</dt>
            <dd>{fiche.date_prochaine_epreuve_etancheite ?? "—"}</dd>
          </div>
        </dl>

        {fiche.notes && (
          <dl className="carte">
            <h3>Notes</h3>
            <div className="carte__champ">
              <dd>{fiche.notes}</dd>
            </div>
          </dl>
        )}
      </div>

      {peutModifier && (
        <div className="grille-cartes">
          <div className="carte">
            <h3>Fiche GRV</h3>
            <ModificationFiche fiche={fiche} estAdmin={estAdmin} />
          </div>
        </div>
      )}
    </div>
  );
}

function ModificationFiche({ fiche, estAdmin }: { fiche: FicheGrv; estAdmin: boolean }) {
  const modifier = useMettreAJourGrv(fiche.numero_grv);
  const produits = useProduitsTransportes();
  // Réservé à l'administrateur côté serveur (`GET /v1/admin/referentiels/client`) : `enabled` évite
  // qu'un opérationnel déclenche cet appel pour un menu qu'il ne verra de toute façon jamais.
  const clients = useReferentiel("client", { enabled: estAdmin });
  const [enEdition, definirEnEdition] = useState(false);

  const produitsActifs = produits.data?.data ?? [];
  const clientsActifs = Object.entries(clients.data?.data ?? {}).map(([code, valeur]) => ({
    code,
    libelle: valeur.libelle,
  }));

  const codeProduitActuel = resoudreCode(fiche.produit_transporte ?? "", produitsActifs);
  const codeClientActuel = resoudreCode(fiche.client_id, clientsActifs);

  const [valeurs, definirValeurs] = useState({
    client_id: codeClientActuel,
    produit_transporte: codeProduitActuel,
    date_mise_en_service: fiche.date_mise_en_service ?? "",
    date_derniere_epreuve_etancheite: fiche.date_derniere_epreuve_etancheite ?? "",
    date_prochaine_epreuve_etancheite: fiche.date_prochaine_epreuve_etancheite ?? "",
    notes: fiche.notes ?? "",
  });

  const optionsProduits = optionsAvecValeurCourante(produitsActifs, codeProduitActuel);
  const optionsClients = optionsAvecValeurCourante(clientsActifs, codeClientActuel);

  function ouvrir() {
    definirValeurs({
      client_id: codeClientActuel,
      produit_transporte: codeProduitActuel,
      date_mise_en_service: fiche.date_mise_en_service ?? "",
      date_derniere_epreuve_etancheite: fiche.date_derniere_epreuve_etancheite ?? "",
      date_prochaine_epreuve_etancheite: fiche.date_prochaine_epreuve_etancheite ?? "",
      notes: fiche.notes ?? "",
    });
    definirEnEdition(true);
  }

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    await modifier.mutateAsync({
      client_id: estAdmin ? valeurs.client_id : undefined,
      produit_transporte: valeurs.produit_transporte || null,
      date_mise_en_service: valeurs.date_mise_en_service || null,
      date_derniere_epreuve_etancheite: valeurs.date_derniere_epreuve_etancheite || null,
      date_prochaine_epreuve_etancheite: valeurs.date_prochaine_epreuve_etancheite || null,
      notes: valeurs.notes || null,
    });
    definirEnEdition(false);
  }

  // Tant que les référentiels des menus déroulants n'ont pas fini de charger, `ouvrir()` figerait
  // la valeur actuelle de la fiche sans pouvoir la résoudre vers son code (`resoudreCode` ne trouve
  // rien dans une liste encore vide) : le menu s'ouvrirait sur « — » au lieu de la bonne option.
  const referentielsPrets = produits.isSuccess && (!estAdmin || clients.isSuccess);

  if (!enEdition) {
    return referentielsPrets ? (
      <button type="button" className="bouton-principal" onClick={ouvrir}>
        Modifier
      </button>
    ) : (
      <Chargement libelle="Chargement…" />
    );
  }

  return (
    <form onSubmit={(e) => void soumettre(e)}>
      <div className="formulaire-grille">
        {estAdmin && (
          <label>
            Client
            <select
              required
              value={valeurs.client_id}
              onChange={(e) => definirValeurs((v) => ({ ...v, client_id: e.target.value }))}
            >
              {optionsClients.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.libelle}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Produit transporté
          <select
            value={valeurs.produit_transporte}
            onChange={(e) =>
              definirValeurs((v) => ({ ...v, produit_transporte: e.target.value }))
            }
          >
            <option value="">—</option>
            {optionsProduits.map((option) => (
              <option key={option.code} value={option.code}>
                {option.libelle}
              </option>
            ))}
          </select>
        </label>
        <label>
          Mise en service
          <input
            type="date"
            value={valeurs.date_mise_en_service}
            onChange={(e) =>
              definirValeurs((v) => ({ ...v, date_mise_en_service: e.target.value }))
            }
          />
        </label>
        <label>
          Dernière épreuve d'étanchéité
          <input
            type="date"
            value={valeurs.date_derniere_epreuve_etancheite}
            onChange={(e) =>
              definirValeurs((v) => ({ ...v, date_derniere_epreuve_etancheite: e.target.value }))
            }
          />
        </label>
        <label>
          Prochaine épreuve d'étanchéité
          <input
            type="date"
            value={valeurs.date_prochaine_epreuve_etancheite}
            onChange={(e) =>
              definirValeurs((v) => ({ ...v, date_prochaine_epreuve_etancheite: e.target.value }))
            }
          />
        </label>
        <label>
          Notes
          <input
            value={valeurs.notes}
            onChange={(e) => definirValeurs((v) => ({ ...v, notes: e.target.value }))}
          />
        </label>
      </div>
      <div className="formulaire-actions">
        <button type="submit" className="bouton-principal" disabled={modifier.isPending}>
          Enregistrer
        </button>
        <button type="button" onClick={() => definirEnEdition(false)}>
          Annuler
        </button>
      </div>
      {modifier.isError && (
        <p role="alert" className="formulaire-ligne__erreur">
          {modifier.error instanceof ErreurApi ? modifier.error.message : "La modification a échoué."}
        </p>
      )}
    </form>
  );
}
